
import { instantFromZonedParts } from '@shared/tz';
import { viewerTimeZone } from '@client/shared/lib/viewer-timezone.ts';

export interface LocalSlot {
  date: string;
  minutes: number;
}

function parseDay(date: string): { year: number; month: number; day: number } {
  const [y, m, d] = date.split('-').map(Number);
  return { year: y ?? 0, month: m ?? 1, day: d ?? 1 };
}

export function localRangeToInstants(
  date: string,
  startMinutes: number,
  endMinutes: number,
  timeZone: string = viewerTimeZone(),
): { start: string; end: string } {
  const day = parseDay(date);
  const start = instantFromZonedParts(
    { ...day, hour: Math.floor(startMinutes / 60), minute: startMinutes % 60 },
    timeZone,
  );
  const end = instantFromZonedParts(
    { ...day, hour: Math.floor(endMinutes / 60), minute: endMinutes % 60 },
    timeZone,
  );
  return { start: start.toISOString(), end: end.toISOString() };
}

// A bare calendar date (YYYY-MM-DD, e.g. bookingUntil) → "Fri, Jul 15". No viewer-tz shift:
// a noon-UTC anchor formatted in UTC keeps the calendar day exactly as given.
export function formatCalendarDate(date: string): string {
  const { year, month, day } = parseDay(date);
  const anchor = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(anchor);
}

export function formatSlotLabel(minutes: number): string {
  const total = ((minutes % 1440) + 1440) % 1440;
  const h24 = Math.floor(total / 60);
  const m = total % 60;
  const ampm = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

export function todayInZone(timeZone: string = viewerTimeZone()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function addDays(date: string, n: number): string {
  const { year, month, day } = parseDay(date);
  const d = new Date(Date.UTC(year, month - 1, day + n));
  const yy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

export function dayHeaderLabel(date: string, timeZone: string = viewerTimeZone()): string {
  const { year, month, day } = parseDay(date);
  const anchor = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
  }).format(anchor);
}
